import '@app/App.css';
import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { ObjectInfos } from "@dto/objects";
import { FOLDER_SEPARATOR, MAX_FOLDER_DEPTH } from "@dto/preferences";
import { FullRightAccess, hasGlobalRightAccess, ViewRightType } from "@dto/Users";
import { View } from "@dto/views";
import { zodResolver } from "@hookform/resolvers/zod";
import { BASE_URL } from "@utils/constants";
import { updateStorageSelection } from "@utils/localStorage";
import { toastError } from "@utils/toasts";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ErrorMessage, TW_ViewMenuItem, TW_ViewMenuItemIcon, useHover } from "@utils/twClasses";
import { del } from "@utils/utils";
import { depthOf, folderDeletionSummary, deleteFolderToRoot, folderName, pruneAfterViewsDeletion, renameFolder } from "@utils/viewFolders";
import { Edit2, FolderPlus, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate, translateHtml } from "src/i18n/i18n";
import { useModelStore, useUserStore } from "src/store";
import { z } from "zod";
import { moveViewToFolder } from "@utils/viewFolders";
import { NewViewForm } from "./NewViewButton";
import { useViewFolders } from "./useViewFolders";
import ViewFolderForm, { ViewFolderCreationHeader } from "./ViewFolderForm";

/**
 * Items of a personal view folder menu: add subfolder, rename, delete.
 * Folders are personal, no special right is required; only the destructive
 * deletion of the contained views is gated per view.
 */

interface FolderItemProps {
    objectInfos: ObjectInfos;
    path: string;
    closeMenu: () => void;
}

// --- New view inside the folder --------------------------------------------

// Creates a shared view (same right as the sidebar "Ajouter une vue" button),
// files it directly into this folder and selects it so the user configures it
// right away. Hidden when the user cannot create views.
export function ViewFolderMenuNewViewItem({ objectInfos, path, closeMenu }: FolderItemProps) {

    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const wsHoverColor = useModelStore(state => state.wsHoverColor);
    const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));
    const { prefs, saveFolders } = useViewFolders(objectInfos.object._key);
    const user = useUserStore(state => state.user);
    const userRole = useModelStore(state => state.userRole);
    const workspace = useModelStore(state => state.selectedWorkspace);
    const allObjectInfos = useModelStore(state => state.objectInfos);
    const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
    const updateObjectInfos = useModelStore(state => state.updateObjectInfos);

    const hasViewFullRight = hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess);

    const viewCreated = async (view: View) => {
        // File the new view into this folder first: when the selection then
        // changes, the sidebar auto-expands the folder chain
        await saveFolders(moveViewToFolder(prefs, view._key, path));

        // Add the view to the model state and select it (shown right away
        // for configuration), like the sidebar "Ajouter une vue" button
        const newViews = [...(objectInfos.views ?? []), view];
        const newObjectInfo = { ...objectInfos, views: newViews };
        const newAllObjectInfos = allObjectInfos.map(o => o.object._key === objectInfos.object._key ? newObjectInfo : o);
        updateObjectInfos(newAllObjectInfos, newObjectInfo, view, null, sharedObjectInfos);
        updateStorageSelection(workspace?._key, newObjectInfo.object?._key, view._key, null);

        setIsDialogOpen(false);
        closeMenu();
    }

    function click(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        event.stopPropagation();
        setIsDialogOpen(true);
    }

    function onOpenChange(open: boolean) {
        setIsDialogOpen(open);
        if (!open) {
            closeMenu();
        }
    }

    if (!hasViewFullRight) {
        return null;
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
            <DialogTrigger className={TW_ViewMenuItem} {...buttonHover} onClick={click}>
                <Plus className={TW_ViewMenuItemIcon} />{t("folder.button.newView.text")}
            </DialogTrigger>
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle>{t("view.dialog.new.title")}</DialogTitle>
                    <DialogDescription className="hidden">{t("view.dialog.new.description")}</DialogDescription>
                </DialogHeader>
                <NewViewForm objectInfos={objectInfos} onNewView={viewCreated} />
            </DialogContent>
        </Dialog>
    )
}

// --- Add a subfolder -------------------------------------------------------

export function ViewFolderMenuAddSubItem({ objectInfos, path, closeMenu }: FolderItemProps) {

    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const wsHoverColor = useModelStore(state => state.wsHoverColor);
    const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));
    const disabled = depthOf(path) >= MAX_FOLDER_DEPTH;

    function click(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) {
            return;
        }
        setIsDialogOpen(true);
    }

    function cancel(event: React.MouseEvent<HTMLButtonElement> | undefined) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        setIsDialogOpen(false);
        closeMenu();
    }

    function onOpenChange(open: boolean) {
        setIsDialogOpen(open);
        if (!open) {
            closeMenu();
        }
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
            <DialogTrigger className={TW_ViewMenuItem + ` ${disabled ? "item-disabled" : ""}`} {...buttonHover} onClick={click}>
                <FolderPlus className={TW_ViewMenuItemIcon} />{t("folder.button.new.sub.text")}
            </DialogTrigger>
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <ViewFolderCreationHeader />
                <ViewFolderForm objectKey={objectInfos.object._key} parentPath={path} onDone={() => { setIsDialogOpen(false); closeMenu(); }} onCancel={cancel} />
            </DialogContent>
        </Dialog>
    )
}

// --- Rename ----------------------------------------------------------------

export function ViewFolderMenuRenameItem({ objectInfos, path, closeMenu }: FolderItemProps) {

    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const wsHoverColor = useModelStore(state => state.wsHoverColor);
    const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));

    function click(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        event.stopPropagation();
        setIsDialogOpen(true);
    }

    function cancel(event: React.MouseEvent<HTMLButtonElement> | undefined) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        setIsDialogOpen(false);
        closeMenu();
    }

    function onOpenChange(open: boolean) {
        setIsDialogOpen(open);
        if (!open) {
            closeMenu();
        }
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
            <DialogTrigger className={TW_ViewMenuItem} {...buttonHover} onClick={click}>
                <Edit2 className={TW_ViewMenuItemIcon} />{t("folder.button.rename.text")}
            </DialogTrigger>
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle>{t("folder.dialog.rename.title")}</DialogTitle>
                    <DialogDescription className="hidden">{t("folder.dialog.new.description")}</DialogDescription>
                </DialogHeader>
                <ViewFolderRenameForm objectKey={objectInfos.object._key} path={path} onDone={() => { setIsDialogOpen(false); closeMenu(); }} onCancel={cancel} />
            </DialogContent>
        </Dialog>
    )
}

interface RenameFormProps {
    objectKey: string;
    path: string;
    onDone: () => void;
    onCancel: (event: React.MouseEvent<HTMLButtonElement> | undefined) => void;
}

function ViewFolderRenameForm({ objectKey, path, onDone, onCancel }: RenameFormProps) {

    const { t } = useTranslation();
    const { prefs, saveFolders } = useViewFolders(objectKey);
    const [error, setError] = useState<string | null>(null);
    const wsColor = useModelStore(state => state.wsColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    const formSchema = z.object({
        folderName: z.string().min(1, {
            message: t("folder.dialog.error.name.missing"),
        }).refine(name => !name.includes(FOLDER_SEPARATOR), {
            message: t("folder.dialog.error.name.separator"),
        }),
    })

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            folderName: folderName(path),
        },
    })

    function onSubmit(values: z.infer<typeof formSchema>) {
        if (values.folderName.trim() === folderName(path)) {
            onDone();
            return;
        }
        const renamed = renameFolder(prefs, path, values.folderName);
        if (!renamed) {
            setError(t("folder.dialog.error.name.invalid"));
            return;
        }
        saveFolders(renamed).then(success => {
            if (success) {
                setError(null);
                onDone();
            }
        })
    }

    function onEnterKeyPress(event: React.KeyboardEvent) {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit(onSubmit)();
        }
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                    control={form.control}
                    name="folderName"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{t("folder.dialog.rename.label")}</FormLabel>
                            <FormControl>
                                <Input placeholder={t("folder.dialog.name.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                {error && <div className={TW_ErrorMessage}>{error}</div>}
                {!error && <div className={TW_ErrorMessage}></div>}
                <div className={TW_ButtonList}>
                    <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
                    <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.confirm.text")}</Button>
                </div>
            </form>
        </Form>
    )
}

// --- Delete (two-choice dialog) --------------------------------------------

export function ViewFolderMenuDeleteItem({ objectInfos, path, closeMenu }: FolderItemProps) {

    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const wsHoverColor = useModelStore(state => state.wsHoverColor);
    const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));
    const wsColor = useModelStore(state => state.wsColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const dialogButtonHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const dialogButtonHover2 = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const dialogButtonHover3 = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    const { prefs, saveFolders } = useViewFolders(objectInfos.object._key);
    const user = useUserStore(state => state.user);
    const userRole = useModelStore(state => state.userRole);
    const workspace = useModelStore(state => state.selectedWorkspace);
    const allObjectInfos = useModelStore(state => state.objectInfos);
    const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const setObjectInfos = useModelStore(state => state.updateObjectInfos);

    // Same predicate as the view delete menu item (the backend re-checks)
    const canDeleteView = (view: View) =>
        view.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess, view._key);

    const summary = folderDeletionSummary(prefs, path, objectInfos.views ?? [], canDeleteView);
    const canDeleteSomeViews = summary.deletableViews.length > 0 && !deleting;

    function click(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        event.stopPropagation();
        setIsDialogOpen(true);
    }

    function cancel(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        event.stopPropagation();
        setIsDialogOpen(false);
        closeMenu();
    }

    function onOpenChange(open: boolean) {
        setIsDialogOpen(open);
        if (!open) {
            closeMenu();
        }
    }

    // Views go back to the root, the folder and its subtree disappear
    function deleteToRoot(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        event.stopPropagation();
        saveFolders(deleteFolderToRoot(prefs, path)).then(() => {
            setIsDialogOpen(false);
            closeMenu();
        });
    }

    // Destructive branch: delete the views the user is allowed to delete, keep
    // the others (and the folder chain sheltering them)
    async function deleteWithViews(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        event.stopPropagation();
        if (!workspace?._key || deleting) {
            return;
        }
        setDeleting(true);
        try {
            const results = await Promise.all(summary.deletableViews.map(async view => {
                const url = `${BASE_URL}/model/workspaces/${workspace._key}/objects/${objectInfos.object._key}/views/${view._key}`;
                const response = await del(url, null);
                const failed = response !== null && isCompozzError(response);
                if (failed) {
                    const cError = response as CompozzError;
                    console.error(`Error deleting view ${view._key}: ${cError.message}`);
                }
                return { view, failed };
            }));
            const deletedKeys = new Set(results.filter(r => !r.failed).map(r => r.view._key));
            const failedCount = results.filter(r => r.failed).length;
            const survivors = [
                ...summary.protectedViews.map(v => v._key),
                ...results.filter(r => r.failed).map(r => r.view._key),
            ];

            await saveFolders(pruneAfterViewsDeletion(prefs, path, survivors));

            if (deletedKeys.size > 0) {
                removeViewsFromState(deletedKeys);
            }
            if (failedCount > 0) {
                toastError(translate(t, "folder.delete.partial.toast", [failedCount]));
            }
        } finally {
            setDeleting(false);
            setIsDialogOpen(false);
            closeMenu();
        }
    }

    function removeViewsFromState(deletedKeys: Set<string>) {
        const remainingViews = (objectInfos.views ?? []).filter(v => !deletedKeys.has(v._key));
        const newObjectInfo = { ...objectInfos, views: remainingViews };
        const newAllObjectInfos = allObjectInfos.map(o => o.object._key === objectInfos.object._key ? newObjectInfo : o);

        let newSelectedObjectInfos = selectedObjectInfos;
        if (newSelectedObjectInfos?.object._key === objectInfos.object._key) {
            newSelectedObjectInfos = newObjectInfo;
        }
        let newSelectedView = selectedView;
        if (newSelectedView && deletedKeys.has(newSelectedView._key)) {
            newSelectedView = remainingViews.length > 0 ? remainingViews[0] : null;
        }
        setObjectInfos(newAllObjectInfos, newSelectedObjectInfos, newSelectedView, null, sharedObjectInfos);
        updateStorageSelection(workspace?._key, newSelectedObjectInfos?.object?._key, newSelectedView?._key, null);
    }

    const hasViews = summary.views.length > 0;
    return (
        <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
            <DialogTrigger className={TW_ViewMenuItem} {...buttonHover} onClick={click}>
                <Trash2 className={TW_ViewMenuItemIcon} />{t("folder.button.delete.text")}
            </DialogTrigger>
            <DialogContent onOpenAutoFocus={(event) => event.preventDefault()} onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle>{t("folder.dialog.delete.title")}</DialogTitle>
                    <DialogDescription>
                        {hasViews
                            ? translateHtml(t, "folder.dialog.delete.description", [folderName(path), summary.subFolderCount, summary.views.length])
                            : translateHtml(t, "folder.dialog.delete.empty.description", [folderName(path), summary.subFolderCount])}
                    </DialogDescription>
                </DialogHeader>
                {summary.protectedViews.length > 0 &&
                    <div className="text-sm text-gray-500">
                        {translate(t, "folder.dialog.delete.protected.text", [summary.protectedViews.length])}
                    </div>
                }
                <div className={TW_ButtonList}>
                    <Button className={TW_AppDialogButton} onClick={cancel} {...dialogButtonHover}>{t("general.button.cancel.text")}</Button>
                    {hasViews ?
                        <>
                            <Button className={TW_AppDialogButton} onClick={deleteToRoot} {...dialogButtonHover2}>{t("folder.dialog.delete.toRoot.text")}</Button>
                            {/* Explicitly grayed when no view can be deleted: the
                                workspace inline style would otherwise keep the
                                button looking active */}
                            <Button
                                className={TW_AppDialogButton + (canDeleteSomeViews ? "" : " opacity-50 cursor-not-allowed")}
                                onClick={deleteWithViews}
                                disabled={!canDeleteSomeViews}
                                {...(canDeleteSomeViews ? dialogButtonHover3 : { style: { backgroundColor: "#e5e7eb", color: "#9ca3af" } })}
                            >
                                {translate(t, "folder.dialog.delete.withViews.text", [summary.deletableViews.length, summary.views.length])}
                            </Button>
                        </>
                        :
                        <Button className={TW_AppDialogButton} onClick={deleteToRoot} {...dialogButtonHover2}>{t("general.button.confirm.text")}</Button>
                    }
                </div>
            </DialogContent>
        </Dialog>
    )
}
